<template>
  <div class="payments-wrapper">
    <!-- Search Bar for Invoice Number -->
    <v-row>
      <v-col :cols="isMobile ? 12 : 3">
        <div class="mb-4">
          <v-text-field
            v-model="searchQuery"
            label="Search by Invoice Number"
            prepend-inner-icon="mdi-magnify"
            clearable
            dense
            outlined
            hide-details
            class="rounded-lg"
          ></v-text-field>
        </div>
      </v-col>
    </v-row>

    <!-- MOBILE VIEW (isMobile = true) -->
    <div v-if="isMobile" class="mobile-payments-container">
      <template v-if="filteredPayments && filteredPayments.length">
        <v-card
          v-for="(item, index) in filteredPayments"
          :key="item.id || index"
          elevation="2"
          class="rounded-xl mb-3 pa-1 mobile-payment-card border-light"
        >
          <!-- Top Row: Checkbox + Date Paid + Invoice Badge -->
          <div class="d-flex justify-space-between align-center px-3 pt-2">
            <div class="d-flex align-center">
              <v-checkbox
                v-model="selectedItems"
                @change="getSelectedPaymentsMobile"
                :value="item"
                dense
                hide-details
                class="mr-2 mt-0 pt-0"
              ></v-checkbox>
              <v-icon small color="grey darken-1" class="mr-1"
                >mdi-calendar-clock</v-icon
              >
              <span
                class="text-body-2 font-weight-medium grey--text text--darken-3"
              >
                {{ formatDate(item.date_paid) }}
              </span>
            </div>

            <v-chip
              small
              label
              color="blue-grey lighten-5"
              class="blue-grey--text text--darken-3 font-weight-medium"
            >
              #{{ item.invoice_number }}
            </v-chip>
          </div>

          <v-divider class="my-2 border-dashed"></v-divider>

          <!-- Content Row: Payment Method + Amount Paid -->
          <v-card-text
            class="py-1 px-3 d-flex justify-space-between align-center"
          >
            <div class="d-flex flex-column">
              <span class="text-caption grey--text text--darken-1"
                >Payment Method</span
              >
              <span
                class="text-body-2 font-weight-medium grey--text text--darken-3 text-capitalize"
              >
                {{ item.payment_method || "—" }}
              </span>
            </div>

            <!-- Amount Highlighted -->
            <div class="text-right">
              <span class="text-caption grey--text text--darken-1 d-block"
                >Amount</span
              >
              <span class="text-subtitle-1 font-weight-black success--text">
                {{ formatCurrency(item.amount_paid) }}
              </span>
            </div>
          </v-card-text>
        </v-card>
      </template>

      <!-- Mobile Empty State -->
      <v-card
        v-else
        flat
        class="rounded-xl border pa-6 text-center grey lighten-5"
      >
        <v-icon large color="grey lighten-1" class="mb-2"
          >mdi-cash-remove</v-icon
        >
        <div class="text-body-2 grey--text text--darken-1">
          No payments recorded for this profile yet.
        </div>
      </v-card>
    </div>

    <!-- DESKTOP / TABLET VIEW (isMobile = false) -->
    <v-data-table
      v-else
      :headers="paymentHeaders"
      :items="filteredPayments"
      class="border rounded-lg"
      :items-per-page="5"
      :footer-props="{
        'items-per-page-options': [5, 10, 20, -1],
      }"
      no-data-text="No payments recorded for this profile yet."
    >
      <!-- Selection Checkbox Column Template -->
      <template #[`item.selection`]="{ item }">
        <v-checkbox
          @change="getSelectedPayments($event, item)"
          dense
          hide-details
          class="mt-0 pt-0"
        ></v-checkbox>
      </template>

      <template #[`item.date_paid`]="{ item }">
        <span class="text-body-2 font-weight-medium grey--text text--darken-3">
          {{ formatDate(item.date_paid) }}
        </span>
      </template>

      <template #[`item.amount_paid`]="{ item }">
        <span class="text-body-2 font-weight-bold success--text">
          {{ formatCurrency(item.amount_paid) }}
        </span>
      </template>

      <template #[`item.invoice_number`]="{ item }">
        <v-chip
          small
          label
          color="blue-grey lighten-5"
          class="blue-grey--text text--darken-3 font-weight-medium"
        >
          #{{ item.invoice_number }}
        </v-chip>
      </template>

      <template #[`item.payment_method`]="{ item }">
        <span class="text-caption grey--text text--darken-2">
          {{ item.payment_method || "—" }}
        </span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import helpersJs from "@/plugins/helpers";

export default {
  props: {
    clientPayments: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    isMobile() {
      return this.$store.getters.isMobile;
    },
    getCurrency() {
      return this.$store.getters.getCurrency;
    },
    filteredPayments() {
      if (!this.clientPayments) return [];
      if (!this.searchQuery) return this.clientPayments;

      const query = this.searchQuery.toString().toLowerCase().trim();
      return this.clientPayments.filter((item) => {
        const invoiceNum = item.invoice_number
          ? item.invoice_number.toString().toLowerCase()
          : "";
        return invoiceNum.includes(query);
      });
    },
    paymentHeaders() {
      return [
        {
          text: "",
          value: "selection",
          sortable: false,
          width: "48px",
          align: "center",
        },
        { text: "Date", value: "date_paid", align: "start", sortable: true },
        { text: "Invoice Reference", value: "invoice_number", align: "start" },
        { text: "Payment Method", value: "payment_method", align: "start" },
        { text: "Amount", value: "amount_paid", align: "end", sortable: true },
      ];
    },
  },
  data() {
    return {
      searchQuery: "",
      selectedItems: [],
    };
  },
  methods: {
    getSelectedPaymentsMobile(selectedItems) {
      this.$emit("payment-selected", [...this.selectedItems]);
    },
    getSelectedPayments(checked, item) {
      const index = this.selectedItems.findIndex((payment) => {
        return payment.id === item.id;
      });

      const notExist = index === -1;
      const exist = index !== -1;

      if (!checked && exist) {
        this.selectedItems.splice(index, 1);
        this.$emit("payment-selected", [...this.selectedItems]);
        return;
      }

      if (checked && exist) {
        this.$emit("payment-selected", [...this.selectedItems]);
        return;
      }

      if (checked && notExist) {
        this.selectedItems.push(item);
        this.$emit("payment-selected", [...this.selectedItems]);
        return;
      }
    },
    formatCurrency(amount) {
      const validAmount = Number(amount) || 0;
      return `${this.getCurrency}${helpersJs.formatMoney(validAmount)}`;
    },
    formatDate(dateString) {
      if (!dateString) return "—";
      return new Date(dateString).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.mobile-payment-card {
  border: 1px solid #f0f0f0;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.mobile-payment-card:active {
  transform: scale(0.985);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
}

.border-dashed {
  border-style: dashed !important;
  border-color: #e0e0e0 !important;
}

.border-light {
  border: 1px solid #e8e8e8 !important;
}
</style>
